export enum TicketStatus {
  ON_HOLD = "on_hold",
  TODO = "to_do",
  IN_PROGRESS = "in_progress",
  COMPLETED = "completed",
  CANCELLED = "cancelled"
}

export enum TicketPriority {
  NONE = "none",
  LOW = "low",
  MEDIUM = "medium",
  HIGH = "high",
  URGENT = "urgent"
}

export enum TaskState {
  UNCOMPLETED = "uncompleted",
  COMPLETED = "completed"
}

export enum TaskActionType {
  UPLOAD_FILE = "upload_file",
  RATING = "rating",
  FREE_TEXT = "free_text",
  URL = "url"
}

export enum TicketActivityType {
  TICKET_CREATED = "ticket_created",
  STATUS_CHANGED = "status_changed",
  PRIORITY_CHANGED = "priority_changed",
  TITLE_CHANGED = "title_changed",
  DESCRIPTION_CHANGED = "description_changed",
  TAGS_CHANGED = "tags_changed",
  ASSIGNEES_CHANGED = "assignees_changed",
  USER_CHANGED = "user_changed",
  DUE_DATE_CHANGED = "due_date_changed",
  LOCATION_CHANGED = "location_changed",
  NOTE_ADDED = "note_added",
  NOTE_EDITED = "note_edited",
  NOTE_DELETED = "note_deleted",
  TASK_ADDED = "task_added",
  TASK_DELETED = "task_deleted",
  TASK_EDITED = "task_edited",
  TASK_STATE_CHANGED = "task_state_changed",
  TASK_ACTION_UPDATED = "task_action_updated",
  TASKS_REORDERED = "tasks_reordered",
  /** Ticket claimed by a member from the open-to-group lane via /accept. */
  TICKET_CLAIMED = "ticket_claimed",
  /**
   * Bot escalated the conversation to a human team — recorded when an
   * escalation opens or re-opens the ticket to a routing group. Rendered
   * with the route icon in the activity feed (e.g. "Bot escalated this
   * ticket. Customer requested a human agent.").
   */
  TICKET_ESCALATED = "ticket_escalated",
  /**
   * Ticket (re)dispatched to a routing group — recorded when the ticket is
   * routed to a group, including a manual group change from the detail
   * panel ("Dispatched to {group} group."). The activity `metadata`
   * carries the target `routingGroupSlug`.
   */
  TICKET_DISPATCHED = "ticket_dispatched",
  /**
   * TKT-KK (ENG-2577) — bot appended new context to this existing ticket
   * because the LLM judge decided the caller's new escalation continues
   * an existing open ticket rather than being a separate issue. The
   * activity row's `metadata` carries the judge's reason + confidence
   * + the source bot conversation id.
   */
  BOT_UPDATED_TICKET = "bot_updated_ticket"
}

export enum ActivityActorType {
  MEMBER = "member",
  QUEST = "quest",
  INTEGRATION = "integration",
  SCHEDULED_JOB = "scheduled_job",
  SYSTEM = "system",
  // Anonymous, token-bound external actor — see AccessTokenService.
  EMAIL_RECIPIENT = "email_recipient"
}

export enum TemplateFollowOnAction {
  SEND_EMAIL = "send_email",
  CREATE_TICKET = "create_ticket",
  DO_BOTH = "do_both"
}

export const ticketStatusLabels: Record<TicketStatus, string> = {
  [TicketStatus.ON_HOLD]: "On Hold",
  [TicketStatus.TODO]: "To Do",
  [TicketStatus.IN_PROGRESS]: "In Progress",
  [TicketStatus.COMPLETED]: "Completed",
  [TicketStatus.CANCELLED]: "Cancelled"
};

export const orderedTicketStatuses: TicketStatus[] = [
  TicketStatus.TODO,
  TicketStatus.IN_PROGRESS,
  TicketStatus.COMPLETED,
  TicketStatus.ON_HOLD,
  TicketStatus.CANCELLED
];

function isTicketStatus(value: string): value is TicketStatus {
  return Object.values(TicketStatus).includes(value as TicketStatus);
}

export function formatTicketStatus(status: string): string {
  return isTicketStatus(status) ? ticketStatusLabels[status] : status;
}

export const ticketPriorityLabels: Record<TicketPriority, string> = {
  [TicketPriority.NONE]: "No Priority",
  [TicketPriority.LOW]: "Low",
  [TicketPriority.MEDIUM]: "Medium",
  [TicketPriority.HIGH]: "High",
  [TicketPriority.URGENT]: "Urgent"
};

// Display order for pickers/filters: No Priority first, then most → least severe.
export const orderedTicketPriorities: TicketPriority[] = [
  TicketPriority.NONE,
  TicketPriority.URGENT,
  TicketPriority.HIGH,
  TicketPriority.MEDIUM,
  TicketPriority.LOW
];

// Severity rank for sorting (higher = more urgent); "No Priority" sinks to the bottom.
export const ticketPrioritySortRank: Record<TicketPriority, number> = {
  [TicketPriority.URGENT]: 4,
  [TicketPriority.HIGH]: 3,
  [TicketPriority.MEDIUM]: 2,
  [TicketPriority.LOW]: 1,
  [TicketPriority.NONE]: 0
};

function isTicketPriority(value: string): value is TicketPriority {
  return Object.values(TicketPriority).includes(value as TicketPriority);
}

export function formatTicketPriority(priority: string): string {
  return isTicketPriority(priority) ? ticketPriorityLabels[priority] : priority;
}

/**
 * Sales-intent ticket tags. A ticket created with any of these tags is a
 * lead-worthy hand-off — the server auto-creates a CRM/FMS lead as a by-product
 * (recorded as an auto-completed TicketTask). Callbacks are deliberately NOT
 * here: a callback is a team task, never a sales lead (us-433 / ENG-2729).
 *
 * Single source of truth for both the bot (which tags the ticket when filing)
 * and the server (which checks tags to fire the lead by-product). Replaces the
 * removed `capture_lead` tool's action set.
 */
export const LEAD_TAGS = ["reservation", "tour", "referral", "lead"] as const;
export type LeadTag = (typeof LEAD_TAGS)[number];

/** True when a ticket's tags include any sales-intent (lead-worthy) tag. */
export function ticketHasLeadTag(tags: readonly string[] | null | undefined): boolean {
  if (!tags) return false;
  return tags.some((t) => (LEAD_TAGS as readonly string[]).includes(t));
}

/**
 * Marker tag the bot adds when the visitor's ONLY interest in the conversation
 * is renting a moving truck (U-Haul etc.). NOT a sales tag — it never makes a
 * ticket lead-worthy by itself; it only lets the server suppress the lead
 * by-product when the location has truck-only lead creation turned off
 * (Location:CortexCreateLeadForMovingTrucks = "0").
 */
export const TRUCK_ONLY_TAG = "truck-rental";

/**
 * True when a lead-worthy ticket is a truck-ONLY inquiry eligible for
 * suppression: it carries the truck marker and its only sales tag is the
 * generic `lead`. Any specific sales tag (reservation / tour / referral)
 * signals real storage intent and wins — the lead is created regardless,
 * even if the model over-applied the truck marker to a mixed conversation.
 */
export function isTruckOnlyLeadCandidate(tags: readonly string[] | null | undefined): boolean {
  if (!tags || !tags.includes(TRUCK_ONLY_TAG)) return false;
  return !tags.some((t) => t !== "lead" && (LEAD_TAGS as readonly string[]).includes(t));
}
